import React, { ChangeEvent, FunctionComponent, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { SlideIn, SubmitButton } from 'components';
import ICONS from 'components/Icons';
import { FormGroup, RadioBlock } from 'components/chroma';

import { CardTypes, CreditCardDetails } from 'scenes/common/types';
import {
  InputFormatQuestion,
  InputQuestion,
} from 'scenes/common/components/Questions';

import {
  validateCardNumber,
  checkDateLength,
  VALID_CARD_NUMBER_LENGTH,
  CARD_NAME_MAX_LENGTH,
  VALID_CARD_NAME_PATTERN,
  CVV_LENGTH,
  validateCardExpiry,
} from 'scenes/common/service/validation';

const CARD_TYPE = 'cardType';
const CARD_NUMBER = 'cardNumber';
const CARD_NAME = 'cardName';
const CARD_EXPIRY = 'cardExpiry';
const CARD_CVV = 'cardCvv';

const CARD_TYPE_OPTIONS = [
  {
    label: '',
    value: CardTypes.Visa,
    icon: (
      <img
        src={ICONS.CARD.VISA}
        alt={`Select ${CardTypes.Visa}`}
        aria-label={CardTypes.Visa}
        height="40"
      />
    ),
  },
  {
    label: '',
    value: CardTypes.Mastercard,
    icon: (
      <img
        src={ICONS.CARD.MASTERCARD}
        alt={`Select ${CardTypes.Mastercard}`}
        aria-label={CardTypes.Mastercard}
        height="40"
      />
    ),
  },
];

const CreditCardPaymentStep: FunctionComponent<{
  onCreditCardPayment: (creditCardDetails: CreditCardDetails) => void;
  buttonText?: string;
  paymentPortal?: boolean;
}> = ({
  onCreditCardPayment,
  buttonText = 'Buy now',
  paymentPortal = false,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    getValues,
    trigger,
  } = useForm({
    mode: 'onChange',
  });

  const onSubmit = useCallback(() => {
    onCreditCardPayment({
      cardNumber: getValues(CARD_NUMBER),
      cardName: getValues(CARD_NAME),
      cardExpiry: getValues(CARD_EXPIRY),
      cardCvv: getValues(CARD_CVV),
      cardType: getValues(CARD_TYPE),
    });
  }, [getValues, onCreditCardPayment]);

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      const { value } = event.target;

      if (!value || value.match(VALID_CARD_NAME_PATTERN)) {
        onChange(value);
      }
    },
    []
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name={CARD_TYPE}
        defaultValue={CardTypes.Unspecified}
        rules={{ required: true }}
        render={({ onChange, value }) => (
          <FormGroup
            id={CARD_TYPE}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(!paymentPortal && {
              label: 'What card type would you like to use?',
            })}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(paymentPortal && {
              legend: 'What card type would you like to use?',
            })}
          >
            <RadioBlock
              id={CARD_TYPE}
              name={CARD_TYPE}
              values={CARD_TYPE_OPTIONS}
              value={value}
              onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                onChange(evt);
                trigger();
              }}
              columns={{ xs: 6 }}
            />
          </FormGroup>
        )}
      />

      {Boolean(getValues(CARD_TYPE)) && (
        <SlideIn>
          <InputFormatQuestion
            size={4}
            errors={errors}
            ariaRequired
            format="#### #### #### ####"
            isNumericString
            control={control}
            stateKey={CARD_NUMBER}
            question="Credit card number"
            defaultValue=""
            validationRules={{
              required: true,
              minLength: VALID_CARD_NUMBER_LENGTH,
              validate: {
                checkNumber: (value) =>
                  validateCardNumber(value, getValues(CARD_TYPE))
                    ? true
                    : 'Card number is not valid. Please try again.',
              },
            }}
          />
        </SlideIn>
      )}

      {Boolean(getValues(CARD_NUMBER)) && !errors?.[CARD_NUMBER] && (
        <SlideIn>
          <InputQuestion
            errors={errors}
            control={control}
            stateKey={CARD_NAME}
            defaultValue=""
            maxLength={CARD_NAME_MAX_LENGTH}
            changeHandler={handleNameChange}
            validationRules={{ required: true }}
            question="Cardholder name"
            ariaRequired
          />
        </SlideIn>
      )}

      {Boolean(getValues(CARD_NAME)) && (
        <SlideIn>
          <InputFormatQuestion
            size={4}
            errors={errors}
            ariaRequired
            format="## / ####"
            mask={['M', 'M', 'Y', 'Y', 'Y', 'Y']}
            isNumericString
            control={control}
            stateKey={CARD_EXPIRY}
            question="Expiry date (MM/YYYY)"
            defaultValue=""
            validationRules={{
              required: true,
              validate: {
                length: (value) => checkDateLength(value),
                expiry: (value) =>
                  validateCardExpiry(value)
                    ? true
                    : 'Please check your expiry date is correct',
              },
            }}
          />
        </SlideIn>
      )}

      {Boolean(getValues(CARD_EXPIRY)) && !errors?.[CARD_EXPIRY] && (
        <SlideIn>
          <InputFormatQuestion
            size={4}
            errors={errors}
            ariaRequired
            format="###"
            isNumericString
            control={control}
            stateKey={CARD_CVV}
            question="CVV"
            defaultValue=""
            validationRules={{
              required: true,
              minLength: CVV_LENGTH,
            }}
          />
        </SlideIn>
      )}

      <SubmitButton enabled={hooksForm.isValid}>{buttonText}</SubmitButton>
    </form>
  );
};

export default CreditCardPaymentStep;
